import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { Button, Chip, Link, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import type { Release } from '@api/api';
import { endpoints, qk } from '../api/endpoints';
import { EmptyState, ErrorState, PageHeader, RelTime, SkeletonRows } from '../components/common';
import { LoadingButton } from '../components/LoadingButton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useNotify } from '../components/Notify';
import { useIsOwner } from '../auth/AuthProvider';
import { MONO } from '../theme';

const STATUS_COLOR = { ready: 'success', fetching: 'info', failed: 'error' } as const;
const NAME_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{0,40}$/;

function CreateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const notify = useNotify();
  const [f, setF] = useState({ name: '', backendRef: 'main', crmRef: 'main', notes: '' });
  const [touched, setTouched] = useState(false);
  const nameErr = !NAME_RE.test(f.name) ? 'Letters, digits, dot, dash; e.g. 2026.10.2' : null;
  const create = useMutation({
    mutationFn: () => endpoints.createRelease({ name: f.name, backendRef: f.backendRef.trim(), crmRef: f.crmRef.trim(), notes: f.notes.trim() || undefined }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: qk.releases });
      notify.ok(`Release ${r.name} is being fetched`);
      setF({ name: '', backendRef: 'main', crmRef: 'main', notes: '' });
      setTouched(false);
      onClose();
    },
    onError: (e) => notify.error(e),
  });
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={(e) => (e.preventDefault(), setTouched(true), !nameErr && f.backendRef && f.crmRef && create.mutate())}>
        <DialogTitle>New release</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField label="Name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} error={touched && !!nameErr} helperText={touched && nameErr ? nameErr : 'Shown on clones, e.g. 2026.10.2'} autoFocus />
            <TextField label="Backend ref" value={f.backendRef} onChange={(e) => setF({ ...f, backendRef: e.target.value })} helperText="Branch, tag or commit of x123-sharing-backend" error={touched && !f.backendRef.trim()} />
            <TextField label="CRM ref" value={f.crmRef} onChange={(e) => setF({ ...f, crmRef: e.target.value })} helperText="Branch, tag or commit of x123-sharing-crm" error={touched && !f.crmRef.trim()} />
            <TextField label="Notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} multiline minRows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <LoadingButton type="submit" variant="contained" loading={create.isPending}>
            Create and fetch
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export function ReleasesPage() {
  const owner = useIsOwner();
  const qc = useQueryClient();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [toDelete, setToDelete] = useState<Release | null>(null);
  const q = useQuery({ queryKey: qk.releases, queryFn: endpoints.releases, refetchInterval: (qq) => (qq.state.data?.some((r) => r.status === 'fetching') ? 5000 : 60_000) });
  const del = useMutation({
    mutationFn: (id: number) => endpoints.deleteRelease(id),
    onSuccess: () => (void qc.invalidateQueries({ queryKey: qk.releases }), notify.ok('Release deleted'), setToDelete(null)),
    onError: (e) => notify.error(e),
  });

  return (
    <>
      <PageHeader
        title="Releases"
        subtitle="Pinned backend + CRM versions that clones are deployed from"
        actions={
          owner && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
              New release
            </Button>
          )
        }
      />
      {q.isError && <ErrorState error={q.error} onRetry={() => void q.refetch()} />}
      {q.isLoading ? (
        <SkeletonRows rows={3} height={52} />
      ) : !q.data?.length ? (
        <EmptyState title="No releases yet" text="A release pins a backend and a CRM ref. The panel fetches them once and every clone builds from that copy." />
      ) : (
        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 2, bgcolor: 'background.paper' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Backend</TableCell>
                <TableCell>CRM</TableCell>
                <TableCell align="right">Used by</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {q.data.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip size="small" color={STATUS_COLOR[r.status]} label={r.status} variant={r.status === 'ready' ? 'outlined' : 'filled'} />
                      {r.fetchJobId && r.status !== 'ready' && (
                        <Link component={RouterLink} to={`/jobs/${r.fetchJobId}`} variant="body2" underline="hover">
                          log
                        </Link>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontFamily: MONO, fontSize: 12.5 }}>
                    {r.backendRef}
                    {r.backendSha && <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}> @{r.backendSha.slice(0, 7)}</Typography>}
                  </TableCell>
                  <TableCell sx={{ fontFamily: MONO, fontSize: 12.5 }}>
                    {r.crmRef}
                    {r.crmSha && <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}> @{r.crmSha.slice(0, 7)}</Typography>}
                  </TableCell>
                  <TableCell align="right">{r.usedBy ? `${r.usedBy} ${r.usedBy === 1 ? 'clone' : 'clones'}` : '—'}</TableCell>
                  <TableCell>
                    <RelTime iso={r.createdAt} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'normal', minWidth: 160, color: 'text.secondary' }}>{r.notes}</TableCell>
                  <TableCell align="right">
                    {owner && (
                      <Tooltip title={r.usedBy ? 'Used by a clone' : 'Delete'}>
                        <span>
                          <IconButton size="small" disabled={r.usedBy > 0} onClick={() => setToDelete(r)} aria-label={`Delete ${r.name}`}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <CreateDialog open={open} onClose={() => setOpen(false)} />
      <ConfirmDialog open={!!toDelete} title={`Delete release ${toDelete?.name}?`} text="Removes the cached code from the server." confirmLabel="Delete" danger busy={del.isPending} onClose={() => setToDelete(null)} onConfirm={() => toDelete && del.mutate(toDelete.id)} />
    </>
  );
}
